import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Check, X } from "lucide-react";

export default function HappinessModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(null);
  const [note, setNote] = useState("");
  const [customAnswers, setCustomAnswers] = useState({});
  const [client, setClient] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user || user.role !== "client" || !user.client_id) return;
    let cancelled = false;
    const check = async () => {
      try {
        const r = await api.get(`/clients/${user.client_id}/ratings/pending`);
        if (cancelled) return;
        if (r.data?.show) {
          const c = await api.get(`/clients/${user.client_id}`);
          if (cancelled) return;
          setClient(c.data);
          setOpen(true);
        }
      } catch (e) {}
    };
    check();
    const id = setInterval(check, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  if (!user || user.role !== "client") return null;
  const form = client?.happiness_form || { rating_enabled: true, notes_enabled: true, dropdowns: [] };
  const closeable = !!client?.happiness_schedule?.closeable;

  const submit = async () => {
    if (form.rating_enabled && !rating) return;
    if (form.rating_enabled && rating !== null && rating <= 6 && form.notes_enabled && !note.trim()) {
      setErr("Please tell us what's causing the issue."); return;
    }
    // require custom dropdowns
    for (const d of form.dropdowns || []) {
      if (!customAnswers[d.id]) { setErr(`Please answer: ${d.label}`); return; }
    }
    setBusy(true); setErr("");
    try {
      await api.post(`/clients/${user.client_id}/ratings`, {
        rating: rating ?? 0, note, custom_answers: customAnswers,
      });
      setOpen(false); setRating(null); setNote(""); setCustomAnswers({});
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const ratingPicked = rating !== null;
  const needsNote = form.rating_enabled && ratingPicked && rating <= 6 && form.notes_enabled;
  const allCustomAnswered = (form.dropdowns || []).every((d) => customAnswers[d.id]);
  const canSubmit = (!form.rating_enabled || ratingPicked) && (!needsNote || note.trim().length > 0) && allCustomAnswered;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (closeable) setOpen(v); }}>
      <DialogContent
        data-testid="happiness-modal"
        className={`bg-[#0F0F0F] border-zinc-800 max-w-md anim-scale-in ${closeable ? "" : "[&>button.absolute]:hidden"}`}
        onPointerDownOutside={(e) => { if (!closeable) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!closeable) e.preventDefault(); }}
      >
        <DialogHeader>
          <div className="t-eyebrow mb-2">Weekly check-in</div>
          <DialogTitle className="font-display text-[20px] tracking-tight font-semibold">How is the work going this week?</DialogTitle>
          <p className="text-zinc-500 text-[12px] pt-1.5">Quick form — your team uses this to prioritise.</p>
        </DialogHeader>

        {form.rating_enabled && (
          <>
            <div className="grid grid-cols-10 gap-1 py-3">
              {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                <button key={n} onClick={() => setRating(n)} data-testid={`rating-${n}`}
                  className={`h-10 rounded text-[13px] font-display font-semibold border transition-all ${
                    rating === n ? "bg-white text-black border-white scale-110"
                      : "bg-[#0a0a0a] text-zinc-400 border-zinc-800 hover:border-zinc-600"
                  }`}>{n}</button>
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-zinc-600 px-1 -mt-1.5 font-mono uppercase tracking-wider">
              <span>Awful</span><span>Excellent</span>
            </div>
          </>
        )}

        {needsNote && (
          <div className="pt-3 anim-fade-up" data-testid="rating-followup">
            <p className="text-[12px] text-zinc-300 mb-2">So sorry to hear that. We'll resolve this within 24 hours. Could you outline what's causing the issue?</p>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              className="bg-[#0a0a0a] border-zinc-800 text-[13px]" data-testid="rating-note" placeholder="Tell us what went wrong…" autoFocus />
          </div>
        )}

        {form.notes_enabled && !needsNote && ratingPicked && (
          <div className="pt-3">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              className="bg-[#0a0a0a] border-zinc-800 text-[13px]" placeholder="Any notes? (optional)" />
          </div>
        )}

        {(form.dropdowns || []).map((d) => (
          <div key={d.id} className="pt-3" data-testid={`happiness-dropdown-${d.id}`}>
            <div className="text-[12px] text-zinc-300 mb-1.5">{d.label}</div>
            <div className="flex flex-wrap gap-1.5">
              {(d.options || []).map((opt) => (
                <button key={opt} type="button" onClick={() => setCustomAnswers({ ...customAnswers, [d.id]: opt })}
                  className={`px-2.5 h-8 rounded text-[12px] border transition-all ${
                    customAnswers[d.id] === opt ? "bg-white text-black border-white"
                      : "bg-transparent text-zinc-300 border-zinc-800 hover:border-zinc-600"
                  }`}>{opt}</button>
              ))}
            </div>
          </div>
        ))}

        {err && <div className="text-[12px] text-[#EF4444] mt-2">{err}</div>}

        <Button onClick={submit} disabled={!canSubmit || busy} data-testid="rating-submit"
          className={`w-full mt-4 h-11 font-semibold text-[13px] ${
            canSubmit ? "bg-white hover:bg-zinc-200 text-black" : "bg-zinc-900 text-zinc-600 cursor-not-allowed"
          }`}>
          {busy ? "Submitting…" : <span className="flex items-center justify-center gap-2"><Check className="w-4 h-4" /> Confirm &amp; submit</span>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
